'use client';

import React from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import {
  LuSearch,
  LuBell,
  LuUser,
  LuSettings,
  LuLogOut,
  LuMenu,
  LuPlus,
  LuCommand,
} from 'react-icons/lu';

import { Box } from './box';
import { Flex } from './flex';
import { HStack, VStack } from './stack';
import { Text } from './text';
import { IconButton } from './icon-button';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { Menu } from './menu';

export interface TopBarProps {
  /**
   * User information for the profile section
   */
  user?: {
    name: string;
    email: string;
    avatar?: string;
    role?: string;
  };
  /**
   * Number of unread notifications
   */
  notificationCount?: number;
  /**
   * Callback when search is triggered
   */
  onSearch?: (query: string) => void;
  /**
   * Callback when notification bell is clicked
   */
  onNotificationClick?: () => void;
  /**
   * Callback when profile is clicked
   */
  onProfileClick?: () => void;
  /**
   * Callback when settings is clicked
   */
  onSettingsClick?: () => void;
  /**
   * Callback when logout is clicked
   */
  onLogout?: () => void;
  /**
   * Callback when mobile menu is toggled
   */
  onMobileMenuToggle?: () => void;
  /**
   * Language switcher component
   */
  languageSwitcher?: React.ReactNode;
  /**
   * Quick action items
   */
  quickActions?: Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }>;
  /**
   * Whether to show the mobile menu button
   */
  showMobileMenu?: boolean;
  /**
   * Custom logo/brand component
   */
  logo?: React.ReactNode;
  /**
   * i18n translations
   */
  translations?: {
    search?: string;
    searchPlaceholder?: string;
    notifications?: string;
    profile?: string;
    settings?: string;
    logout?: string;
    userMenu?: string;
    newNotifications?: string;
    viewAllNotifications?: string;
    quickActions?: string;
    createNew?: string;
  };
}

export function TopBar({
  user,
  notificationCount = 0,
  onSearch,
  onNotificationClick,
  onProfileClick,
  onSettingsClick,
  onLogout,
  onMobileMenuToggle,
  languageSwitcher,
  quickActions = [],
  showMobileMenu = true,
  logo,
  translations = {},
}: TopBarProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);

  // Default translations
  const t = {
    search: translations.search || 'Search...',
    searchPlaceholder: translations.searchPlaceholder || 'Command palette (Ctrl+K)',
    notifications: translations.notifications || 'Notifications',
    profile: translations.profile || 'Profile',
    settings: translations.settings || 'Settings',
    logout: translations.logout || 'Logout',
    userMenu: translations.userMenu || 'User menu',
    newNotifications: translations.newNotifications || 'New notifications',
    viewAllNotifications: translations.viewAllNotifications || 'View all notifications',
    quickActions: translations.quickActions || 'Quick actions',
    createNew: translations.createNew || 'Create new',
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery);
    }
  };

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for search focus
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('topbar-search');
        searchInput?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Flex
      as="header"
      height="64px"
      paddingX={4}
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="white"
      borderBottomWidth="1px"
      borderColor="#E9ECEF"
    >
      {/* Left Section */}
      <HStack gap={4} flex="1">
        {/* Mobile Menu Button */}
        {showMobileMenu && isMobile && (
          <IconButton
            variant="ghost"
            aria-label={t.search}
            onClick={onMobileMenuToggle}
            icon={<LuMenu />}
            size="md"
          />
        )}

        {/* Logo/Brand */}
        {logo && <Box>{logo}</Box>}

        {/* Search Bar */}
        <Box flex="1" maxWidth="100%">
          <form onSubmit={handleSearch}>
            <HStack
              position="relative"
              backgroundColor={isSearchFocused ? 'white' : '#F8F9FA'}
              borderRadius="lg"
              border="1px solid"
              borderColor={isSearchFocused ? '#0066CC' : 'transparent'}
              padding={2}
              transition="all 0.2s"
              _hover={{
                backgroundColor: 'white',
                borderColor: '#E9ECEF',
              }}
            >
              <LuSearch size={20} color="#6C757D" />
              <Input
                id="topbar-search"
                type="search"
                placeholder={isMobile ? t.search : t.searchPlaceholder}
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(e.target.value)
                }
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                variant="subtle"
                inputSize="sm"
                width="100%"
              />
              {!isMobile && (
                <HStack gap={1} position="absolute" right={2}>
                  <Box padding="2px 6px" backgroundColor="#E9ECEF" borderRadius="md" fontSize="xs">
                    <LuCommand size={12} />
                  </Box>
                  <Box padding="2px 6px" backgroundColor="#E9ECEF" borderRadius="md" fontSize="xs">
                    K
                  </Box>
                </HStack>
              )}
            </HStack>
          </form>
        </Box>
      </HStack>

      {/* Right Section */}
      <HStack gap={2}>
        {/* Quick Actions */}
        {quickActions.length > 0 && !isMobile && (
          <Menu.Root>
            <Menu.Trigger>
              <Button variant="ghost" size="sm">
                <LuPlus />
                {!isMobile && <Text marginLeft={2}>{t.createNew}</Text>}
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content minWidth="200px">
                {quickActions.map((action) => (
                  <Menu.Item key={action.id} onClick={action.onClick}>
                    <HStack gap={2}>
                      {action.icon}
                      <Text>{action.label}</Text>
                    </HStack>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}

        {/* Language Switcher */}
        {languageSwitcher}

        {/* Notifications */}
        <Box position="relative">
          <IconButton
            variant="ghost"
            aria-label={t.notifications}
            onClick={onNotificationClick}
            icon={<LuBell />}
            size="md"
          />
          {notificationCount > 0 && (
            <Box position="absolute" top="-2px" right="-2px">
              <Badge size="sm" colorPalette="red">
                {notificationCount > 99 ? '99+' : notificationCount}
              </Badge>
            </Box>
          )}
        </Box>

        {/* User Menu */}
        {user && (
          <Menu.Root>
            <Menu.Trigger>
              <Button variant="ghost" size="sm" aria-label={t.userMenu}>
                <HStack gap={2}>
                  <Box
                    width="32px"
                    height="32px"
                    borderRadius="full"
                    backgroundColor="#0066CC"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontWeight="medium"
                    fontSize="sm"
                  >
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </Box>
                  {!isMobile && (
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="sm" fontWeight="medium" lineHeight="1.2">
                        {user.name}
                      </Text>
                      <Text fontSize="xs" color="#6C757D" lineHeight="1.2">
                        {user.role || user.email}
                      </Text>
                    </VStack>
                  )}
                </HStack>
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content minWidth="200px">
                <Box padding={3} borderBottomWidth="1px" borderColor="#E9ECEF">
                  <Text fontSize="sm" fontWeight="medium">
                    {user.name}
                  </Text>
                  <Text fontSize="xs" color="#6C757D">
                    {user.email}
                  </Text>
                </Box>
                <Menu.Item onClick={onProfileClick}>
                  <HStack gap={2}>
                    <LuUser size={16} />
                    <Text>{t.profile}</Text>
                  </HStack>
                </Menu.Item>
                <Menu.Item onClick={onSettingsClick}>
                  <HStack gap={2}>
                    <LuSettings size={16} />
                    <Text>{t.settings}</Text>
                  </HStack>
                </Menu.Item>
                <Menu.Separator />
                <Menu.Item onClick={onLogout}>
                  <HStack gap={2}>
                    <LuLogOut size={16} />
                    <Text>{t.logout}</Text>
                  </HStack>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      </HStack>
    </Flex>
  );
}

// Export with display name for debugging
TopBar.displayName = 'TopBar';
